import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import XMarkIcon from '@heroicons/react/24/solid/XMarkIcon';
import ArrowDownTrayIcon from '@heroicons/react/24/solid/ArrowDownTrayIcon';
import SparklesIcon from '@heroicons/react/24/solid/SparklesIcon';
import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { WidgetManifestEntry, WidgetPageConfig } from '@/models/widget';
import { GENERAL_DOCS } from '@/pages/widgetEditor/docs/generalDocs';
import {
    buildDocsMarkdown,
    buildDocsSkill,
} from '@/pages/widgetEditor/docs/buildDocsMarkdown';
import { useCopyToClipboard } from '@/pages/widgetEditor/hooks/useCopyToClipboard';
import { buildWidgetDoc } from '@/pages/widgetEditor/utils';
import { WidgetDocSection } from '@/pages/widgetEditor/components/WidgetDocSection';
import {
    DOC_RADIUS,
    DocCode,
    InlineCode,
} from '@/pages/widgetEditor/components/DocCode';

interface WidgetDocsDialogProps {
    open: boolean;
    onClose: () => void;
    widgets: WidgetManifestEntry[];
    /** Widget whose section starts expanded; `null` opens at the general part. */
    widgetId?: string | null;
    /** Absolute URL of the selected version's module entry. */
    entryUrl?: string;
    /** The manifest's `<starjack-config>`, for the AI export. */
    pageConfig?: WidgetPageConfig;
}

/** Hands a generated file to the browser's download. */
const downloadFile = (name: string, content: string) => {
    const url = URL.createObjectURL(
        new Blob([content], { type: 'text/markdown' })
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    link.click();
    URL.revokeObjectURL(url);
};

/**
 * The full documentation: how embedding and this editor work, then one
 * expandable section per widget.
 *
 * Opened either from the documentation card (general first) or from a widget's
 * info button in the rail, which pre-expands that widget.
 */
export const WidgetDocsDialog: FunctionComponent<WidgetDocsDialogProps> = ({
    open,
    onClose,
    widgets,
    widgetId,
    entryUrl,
    pageConfig,
}) => {
    const copyToClipboard = useCopyToClipboard();
    // Built on click, not per render: it walks every widget of the manifest.
    const markdown = () => buildDocsMarkdown(widgets, entryUrl, pageConfig);

    const [expandedId, setExpandedId] = useState<string | null>(
        widgetId ?? null
    );

    /*
     * Re-seeded on every open: the same dialog serves the general card and each
     * widget's info button, so which section is expanded depends on where it
     * was opened from, not on where it was left last time.
     */
    useEffect(() => {
        if (open) setExpandedId(widgetId ?? null);
    }, [open, widgetId]);

    /*
     * Scrolls the requested widget's section into view once it exists in the
     * document. A callback ref rather than a `useEffect` + `scrollIntoView`:
     * MUI's Dialog unmounts its `DialogContent` while closed (default
     * `keepMounted: false`) and remounts it fresh on every open, wrapped in
     * an enter transition and a portal — an effect keyed on `open` can fire
     * before that subtree has actually committed to the DOM, scrolling
     * against a stale or missing node. A callback ref instead runs exactly
     * when React attaches the target `Box` to the document, which is also
     * naturally "on every open" since the node remounts each time. Only
     * assigned to the section that matches `widgetId`, so it never fires for
     * the ten sections nobody asked to see.
     */
    const scrollRequestedSectionIntoView = useCallback(
        (node: HTMLDivElement | null) => {
            node?.scrollIntoView?.({ block: 'start' });
        },
        []
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth='md'
            fullWidth
            scroll='paper'
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                Dokumentation
                <IconButton
                    size='small'
                    aria-label='Dokumentation schließen'
                    onClick={onClose}
                >
                    <XMarkIcon style={{ width: 18, height: 18 }} />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ px: { xs: 2, sm: 4 }, py: 4 }}>
                <Box
                    sx={{
                        mb: 5,
                        p: 2.5,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: DOC_RADIUS,
                        bgcolor: 'action.hover',
                    }}
                >
                    <Typography variant='subtitle2'>
                        Mit KI-Assistenten einbinden
                    </Typography>
                    <Stack spacing={0.5} sx={{ mt: 0.5, mb: 2 }}>
                        <Typography variant='body2' color='text.secondary'>
                            <strong>Markdown:</strong> in ChatGPT, Claude,
                            Copilot oder die Regeln-Datei von Cursor einfügen.
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                            <strong>Skill (Claude Code):</strong> Datei nach{' '}
                            <InlineCode>
                                .claude/skills/starjack-widgets/SKILL.md
                            </InlineCode>{' '}
                            im Website-Projekt legen und eine neue Sitzung
                            starten. Der Assistent nutzt sie dann automatisch,
                            oder direkt mit{' '}
                            <InlineCode>/starjack-widgets</InlineCode>.
                        </Typography>
                    </Stack>
                    <Stack direction='row' sx={{ flexWrap: 'wrap', gap: 1 }}>
                        <Button
                            size='small'
                            variant='outlined'
                            disabled={widgets.length === 0}
                            startIcon={
                                <SparklesIcon
                                    style={{ width: 16, height: 16 }}
                                />
                            }
                            onClick={() => copyToClipboard(markdown())}
                        >
                            Als Markdown kopieren
                        </Button>
                        <Button
                            size='small'
                            variant='outlined'
                            disabled={widgets.length === 0}
                            startIcon={
                                <ArrowDownTrayIcon
                                    style={{ width: 16, height: 16 }}
                                />
                            }
                            onClick={() =>
                                downloadFile(
                                    'SKILL.md',
                                    buildDocsSkill(markdown())
                                )
                            }
                        >
                            Skill herunterladen
                        </Button>
                    </Stack>
                </Box>

                {GENERAL_DOCS.map((section) => (
                    <Box key={section.id} component='section' sx={{ mb: 4 }}>
                        <Typography variant='h6' component='h3' sx={{ mb: 1 }}>
                            {section.title}
                        </Typography>
                        <Typography
                            variant='body2'
                            color='text.secondary'
                            sx={{ lineHeight: 1.7 }}
                        >
                            {section.body}
                        </Typography>
                        {section.code && <DocCode>{section.code}</DocCode>}
                    </Box>
                ))}

                <Divider sx={{ my: 4 }} />

                <Typography variant='h5' component='h2' sx={{ mb: 3 }}>
                    Widgets im Detail
                </Typography>
                {widgets.length === 0 && (
                    <Typography variant='body2' color='text.secondary'>
                        Die Widget-Liste ist derzeit nicht verfügbar.
                    </Typography>
                )}
                <Stack spacing={1.5}>
                    {widgets.map((widget) => (
                        <Box
                            key={widget.widget}
                            ref={
                                widget.widget === widgetId
                                    ? scrollRequestedSectionIntoView
                                    : undefined
                            }
                        >
                            <WidgetDocSection
                                doc={buildWidgetDoc(widget, entryUrl)}
                                expanded={expandedId === widget.widget}
                                onToggle={(isExpanded) =>
                                    setExpandedId(
                                        isExpanded ? widget.widget : null
                                    )
                                }
                            />
                        </Box>
                    ))}
                </Stack>
            </DialogContent>
        </Dialog>
    );
};
